import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { navegarComCtrl } from "../../utils/solicitacoes/navegacao";
import { CustomTable } from "../../componentes/table/CustomTable";
import { CustomDropdown } from "../../componentes/dropdown/CustomDropdown";
import StatusBadge from "../../components/solicitacoes/Shared/StatusBadge";
import GraficosBasicos from "../../components/solicitacoes/Dashboard/GraficosBasicos";
import DashboardProcessoLayout, { Campo, INPUT_CLASS } from "../../components/solicitacoes/Dashboard/DashboardProcessoLayout";
import { useDashboardProcesso } from "../../hooks/solicitacoes/useDashboardProcesso";
import DesligamentoService from "../../services/resource/desligamentoService";
import UsuarioService from "../../services/resource/usuarioService";
import { STATUS_CONFIG } from "../../utils/solicitacoes/statusConfig";
import { kpisBasicos } from "../../utils/solicitacoes/chartOptions";

const service = new DesligamentoService();
const usuarioService = new UsuarioService();

const STATUS_OPTIONS = Object.entries(STATUS_CONFIG).map(([value, info]) => ({ value, label: info.label }));

const MOTIVO_LABELS = {
    PEDIDO_DEMISSAO: "Pedido de Demissão",
    DEMISSAO_SEM_JUSTA_CAUSA: "Demissão sem Justa Causa",
    DEMISSAO_COM_JUSTA_CAUSA: "Demissão com Justa Causa",
    TERMINO_CONTRATO_EXPERIENCIA: "Término de Contrato de Experiência",
    TERMINO_ANTECIPADO_EXPERIENCIA: "Término Antecipado de Experiência",
    TERMINO_CONTRATO_APRENDIZ: "Término de Contrato de Aprendiz",
};
const TIPO_AVISO_LABELS = { AVISO_TRABALHADO: "Aviso Trabalhado", AVISO_INDENIZADO: "Aviso Indenizado", SEM_AVISO: "Sem Aviso" };
const OPCAO_DESLIGAMENTO_LABELS = { SUBSTITUICAO: "Substituição", REDUCAO_QUADRO: "Redução de Quadro" };
const TIPO_SUBSTITUTO_LABELS = { INTERNO: "Interno", EXTERNO: "Externo" };
const MOTIVO_OPTIONS = Object.entries(MOTIVO_LABELS).map(([value, label]) => ({ value, label }));

const simNao = v => v == null ? "-" : (v ? "Sim" : "Não");

const FILTROS_INICIAIS_EXTRA = { funcionarioNome: "", unidadeNome: "", motivoDesligamento: null, status: null, solicitanteId: null };

// A coluna "Setor" foi removida: não existe em DesligamentoDados/DesligamentoListagemDTO — o
// backend nunca a retorna, então sempre renderizava "-".
const COLUNAS = [
    { key: "funcionarioNome", label: "Funcionário" },
    { key: "unidadeNome", label: "Filial" },
    { key: "cargo", label: "Cargo" },
    { key: "motivoDesligamento", label: "Motivo", render: v => MOTIVO_LABELS[v] || v || "-" },
    { key: "dataPrevistaDesligamento", label: "Data Prevista", format: "date" },
    { key: "tipoAviso", label: "Tipo de Aviso", render: v => TIPO_AVISO_LABELS[v] || v || "-" },
    { key: "opcaoDesligamento", label: "Opção de Desligamento", render: v => OPCAO_DESLIGAMENTO_LABELS[v] || v || "-" },
    { key: "tipoSubstituto", label: "Tipo de Substituto", render: v => TIPO_SUBSTITUTO_LABELS[v] || v || "-" },
    { key: "solicitacaoPromocaoSubstitutoNumero", label: "Promoção Vinculada" },
    { key: "pertenceCipa", label: "Pertence à CIPA", render: v => v == null ? "Não consultado" : (v ? "Sim" : "Não"), center: true },
    { key: "justificativa", label: "Justificativa" },
    { key: "justificativaAnalise", label: "Justificativa (Análise RH)" },
    { key: "comentarioAprovacaoGestor", label: "Comentário (Gestor)" },
    { key: "status", label: "Status", render: v => <StatusBadge status={v} />, center: true },
    { key: "etapaAtual", label: "Etapa Atual" },
    { key: "solicitanteNome", label: "Solicitante" },
    { key: "dataSolicitacao", label: "Data", format: "date" },
];

export default function RelatorioDesligamento() {
    const navigate = useNavigate();
    const [usuarios, setUsuarios] = useState([]);

    const { filtros, setFiltros, limparFiltros, pesquisar, linhas, dashboard, loading } = useDashboardProcesso({
        service, chaveFiltros: "solicitacoes.desligamento.filtros", filtrosIniciaisExtra: FILTROS_INICIAIS_EXTRA,
    });

    useEffect(() => {
        document.title = "MyDash | Desligamento de Pessoal";
    }, []);

    useEffect(() => {
        usuarioService.listarUsuarios().then(res => setUsuarios(res.data)).catch(() => setUsuarios([]));
    }, []);

    function setFiltro(campo, valor) {
        setFiltros(prev => ({ ...prev, [campo]: valor }));
    }

    return (
        <DashboardProcessoLayout
            titulo="Desligamento de Pessoal"
            onNovaSolicitacao={() => navigate("/solicitacoes/nova")}
            loading={loading}
            filtros={filtros}
            onFiltroChange={setFiltro}
            onPesquisar={pesquisar}
            onLimparFiltros={limparFiltros}
            kpis={kpisBasicos(dashboard, "fa fa-user-slash")}
            graficos={<GraficosBasicos dashboard={dashboard} />}
            filtrosExtras={(
                <>
                    <Campo label="Funcionário">
                        <input type="text" value={filtros.funcionarioNome} onChange={e => setFiltro("funcionarioNome", e.target.value)}
                            placeholder="Buscar por nome" className={INPUT_CLASS} />
                    </Campo>
                    <Campo label="Filial">
                        <input type="text" value={filtros.unidadeNome} onChange={e => setFiltro("unidadeNome", e.target.value)} className={INPUT_CLASS} />
                    </Campo>
                    <Campo label="Motivo">
                        <CustomDropdown value={filtros.motivoDesligamento} onChange={e => setFiltro("motivoDesligamento", e.value)} options={MOTIVO_OPTIONS}
                            placeholder="Todos os motivos" showClear />
                    </Campo>
                    <Campo label="Status">
                        <CustomDropdown value={filtros.status} onChange={e => setFiltro("status", e.value)} options={STATUS_OPTIONS}
                            placeholder="Todos os status" showClear />
                    </Campo>
                    <Campo label="Solicitante">
                        <CustomDropdown value={filtros.solicitanteId} onChange={e => setFiltro("solicitanteId", e.value)} options={usuarios}
                            optionValue="id" optionLabel="nome" placeholder="Todos os solicitantes" showClear filter />
                    </Campo>
                </>
            )}
        >
            <CustomTable
                data={linhas}
                columns={COLUNAS}
                loading={loading}
                pagination
                pageSize={20}
                exportable
                showHeader={false}
                className="text-[11px] border shadow-card rounded-xl"
                exportFilename="dashboard_desligamento_pessoal"
                footer={() => null}
                onRowSelect={(row, e) => navegarComCtrl(navigate, `/solicitacoes/${row.solicitacaoId}`, e)}
            />
        </DashboardProcessoLayout>
    );
}
